import type { AwsCredentialIdentityProvider, AwsCredentialIdentity } from '@smithy/types';
import { getSettings } from './settings';
import { debugLog, debugError, securityWarn } from '../utils/log';

const TOKEN_KEY = 'midway-id-token';
const RETURN_PATH_KEY = 'midway-return-path';
const NONCE_KEY = 'midway-nonce';

/** Generate a random nonce for OIDC requests. */
function generateNonce(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Decode a JWT's payload (claims) without verifying its signature. Returns
 * null on a malformed token or non-JSON payload. The signature is intentionally
 * not verified here — STS verifies the token cryptographically during
 * AssumeRoleWithWebIdentity; this is for client-side claim reads only.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

/**
 * SEC-018: Validate the OIDC nonce on a token that arrived via the Midway
 * redirect-return URL (hash/query). Compares the token's `nonce` claim against
 * the value persisted by redirectToMidway and consumes (clears) the stored
 * nonce so it is single-use. Returns true only on an exact match.
 *
 * Applies ONLY to the redirect-return path. Tokens from the /api/token
 * silent-auth path never carry a nonce and must NOT be routed through here.
 */
function validateNonce(token: string): boolean {
  const expectedNonce = sessionStorage.getItem(NONCE_KEY);
  // One-time use: clear regardless of outcome so a nonce can never be replayed.
  sessionStorage.removeItem(NONCE_KEY);

  if (!expectedNonce) {
    // A token arrived via the URL but we never initiated a redirect (no pending
    // nonce). This is an unsolicited/forged callback — reject (CWE-352).
    securityWarn('[auth] Rejected redirect token: no pending nonce.');
    return false;
  }

  const claims = decodeJwtPayload(token);
  const actualNonce = typeof claims?.nonce === 'string' ? claims.nonce : null;

  if (!actualNonce) {
    securityWarn('[auth] Rejected redirect token: missing nonce claim.');
    return false;
  }

  if (actualNonce !== expectedNonce) {
    securityWarn('[auth] Rejected redirect token: nonce mismatch.');
    return false;
  }

  return true;
}

/** On page load, extract id_token from hash or query string and store in sessionStorage. */
(function captureTokenFromUrl() {
  // Check hash fragment first (#id_token=...), then query string (?id_token=...)
  const hash = window.location.hash;
  const search = window.location.search;

  let token: string | null = null;

  if (hash) {
    const params = new URLSearchParams(hash.substring(1));
    token = params.get('id_token');
  }

  if (!token && search) {
    const params = new URLSearchParams(search);
    token = params.get('id_token');
  }

  debugLog('[auth] Page load. Token found:', !!token, 'Source:', hash ? 'hash' : search ? 'query' : 'none');

  if (token) {
    // SEC-018: tokens arriving via the Midway redirect carry a nonce. Validate
    // it before trusting/storing the token. Fail closed: on any failure, store
    // nothing and drop any stale token so a forged callback can't leave a
    // previously-valid token in place — getMidwayToken will re-auth on demand.
    if (validateNonce(token)) {
      debugLog('[auth] Storing id_token in sessionStorage. Token length:', token.length);
      sessionStorage.setItem(TOKEN_KEY, token);
    } else {
      sessionStorage.removeItem(TOKEN_KEY);
    }

    // Always restore the return path and scrub the token from the URL/history,
    // regardless of validation outcome (don't leave id_token in the address bar).
    const returnPath = sessionStorage.getItem(RETURN_PATH_KEY) || '/';
    sessionStorage.removeItem(RETURN_PATH_KEY);
    window.history.replaceState(null, '', returnPath);
  }
})();

/** Redirect to Midway login page for authentication. */
function redirectToMidway(midwayEndpoint: string, audience: string): never {
  // Save current path so we can restore it after auth completes
  sessionStorage.setItem(RETURN_PATH_KEY, window.location.pathname + window.location.search);
  const nonce = generateNonce();
  // SEC-018: persist the nonce so it can be validated when the token returns.
  sessionStorage.setItem(NONCE_KEY, nonce);
  const redirectUri = window.location.origin + '/';
  const url = `https://${midwayEndpoint}/SSO/redirect?client_id=${encodeURIComponent(audience)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=id_token&scope=openid&nonce=${encodeURIComponent(nonce)}`;
  window.location.href = url;
  throw new Error('Redirecting to Midway');
}

/** Check if a JWT token is expired (with 60s buffer). */
function isTokenExpired(token: string): boolean {
  const decoded = decodeJwtPayload(token);
  if (!decoded) return true; // malformed → treat as expired
  const exp = decoded.exp;
  if (typeof exp !== 'number') return false; // no/!exp → treat as non-expiring
  return Date.now() / 1000 > exp - 60; // 60s buffer before expiry
}

/** Get a Midway token — from sessionStorage, /api/token, or redirect. */
async function getMidwayToken(midwayEndpoint: string, audience: string): Promise<string> {
  // Check sessionStorage first (survives page reloads and React re-renders)
  const stored = sessionStorage.getItem(TOKEN_KEY);
  if (stored && !isTokenExpired(stored)) {
    debugLog('[auth] Using cached Midway token from sessionStorage');
    return stored;
  }
  if (stored) {
    debugLog('[auth] Cached token expired, clearing');
    sessionStorage.removeItem(TOKEN_KEY);
  }

  debugLog('[auth] No cached token, trying /api/token endpoint...');

  // Try the API token endpoint (works if Midway cookie is already set from prior session)
  const url = `https://${midwayEndpoint}/api/token?client_id=${encodeURIComponent(audience)}`;
  try {
    const response = await fetch(url, { credentials: 'include' });
    if (response.ok) {
      const text = await response.text();
      if (text && text.length > 0) {
        debugLog('[auth] Got token from /api/token');
        sessionStorage.setItem(TOKEN_KEY, text);
        return text;
      }
    }
    debugLog('[auth] /api/token returned non-ok:', response.status);
  } catch (e) {
    debugError('[auth] /api/token fetch failed:', e);
  }

  // No token available — redirect to Midway for authentication
  debugLog('[auth] Redirecting to Midway...');
  redirectToMidway(midwayEndpoint, audience);
}

/** Extract the 'sub' claim from a JWT without verifying the signature. */
function extractSubFromJwt(token: string): string {
  const decoded = decodeJwtPayload(token);
  const sub = decoded?.sub;
  return typeof sub === 'string' && sub ? sub : 'anonymous';
}

/** Exchange a WebIdentityToken for temporary IAM credentials via STS. */
async function assumeRole(token: string, roleArn: string, region: string): Promise<{
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiration: Date;
}> {
  const stsEndpoint = `https://sts.${region}.amazonaws.com`;
  const params = new URLSearchParams({
    Action: 'AssumeRoleWithWebIdentity',
    Version: '2011-06-15',
    RoleArn: roleArn,
    RoleSessionName: extractSubFromJwt(token),
    WebIdentityToken: token,
    DurationSeconds: '3600',
  });

  debugLog('[auth] Calling STS AssumeRoleWithWebIdentity...');
  const response = await fetch(`${stsEndpoint}/?${params.toString()}`);
  debugLog('[auth] STS response status:', response.status);
  if (!response.ok) {
    // Read the error body only in development. In production it is never read,
    // so request/credential details in the STS response can't reach the console
    // (SEC-017 / CWE-532). The thrown error carries only the HTTP status.
    if (import.meta.env.DEV) {
      const errorBody = await response.text().catch(() => 'unable to read body');
      debugError('[auth] STS failed:', response.status, errorBody);
    }
    throw new Error(`STS AssumeRoleWithWebIdentity failed: ${response.status}`);
  }

  const text = await response.text();
  const parser = new DOMParser();
  const xml = parser.parseFromString(text, 'text/xml');

  const getValue = (tag: string) => xml.getElementsByTagName(tag)[0]?.textContent ?? '';

  return {
    accessKeyId: getValue('AccessKeyId'),
    secretAccessKey: getValue('SecretAccessKey'),
    sessionToken: getValue('SessionToken'),
    expiration: new Date(getValue('Expiration')),
  };
}

/**
 * Creates an AwsCredentialIdentityProvider for the Smithy-generated client.
 * Performs Midway OIDC token acquisition + STS AssumeRoleWithWebIdentity.
 * Caches credentials and auto-refreshes when within 5 minutes of expiry.
 */
export function createMidwayCredentialProvider(): AwsCredentialIdentityProvider {
  let cached: AwsCredentialIdentity | null = null;

  return async (): Promise<AwsCredentialIdentity> => {
    if (cached && cached.expiration && cached.expiration > new Date(Date.now() + 5 * 60 * 1000)) {
      return cached;
    }

    const settings = await getSettings();
    const audience = window.location.host;
    const token = await getMidwayToken(settings.midwayEndpoint, audience);
    const stsResult = await assumeRole(token, settings.roleArn, settings.region);

    cached = {
      accessKeyId: stsResult.accessKeyId,
      secretAccessKey: stsResult.secretAccessKey,
      sessionToken: stsResult.sessionToken,
      expiration: stsResult.expiration,
    };

    return cached;
  };
}

/** Clear cached credentials (for logout or error recovery). */
export function clearCredentials(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}
